import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ChakraProvider, extendTheme, Spinner, Center, Text, Button } from '@chakra-ui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SetupWizard from './pages/SetupWizard';
import DashboardPage from './pages/DashboardPage';
import SettingsPage from './pages/SettingsPage';
import ProgressOverviewPage from './pages/ProgressOverviewPage'; // Import the new page
import { lazy } from 'react';
const AuthCallbackPage = lazy(() => import('./pages/AuthCallbackPage'));
import EmailVerificationPage from './pages/EmailVerificationPage';

// Services
import { supabase } from './services/supabase';

// Stores
import { useAuthStore } from './store/authStore';
import { useOptimizedUserStore as useUserStore } from './store/optimizedUserStore';

// Custom theme
const theme = extendTheme({
  colors: {
    brand: {
      50: '#e3f2fd',
      100: '#bbdefb',
      200: '#90caf9',
      300: '#64b5f6',
      400: '#42a5f5',
      500: '#2196f3',
      600: '#1e88e5',
      700: '#1976d2',
      800: '#1565c0',
      900: '#0d47a1',
    },
  },
  fonts: {
    heading: 'Inter, -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
    body: 'Inter, -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
  },
  components: {
    Button: {
      defaultProps: {
        colorScheme: 'blue',
      },
    },
  },
});

// Protected route wrapper
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading: authLoading } = useAuthStore();
  const { profile, profileLoading, fetchUserProfile } = useUserStore(); 
  const location = useLocation();
  const [loadingTooLong, setLoadingTooLong] = useState(false);

  // Setup a timer to detect if loading is taking too long
  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (authLoading || (user && (profileLoading || !profile))) {
      // If still loading after 15 seconds, show recovery option
      timer = setTimeout(() => {
        setLoadingTooLong(true);
      }, 15000); // Extended from 8s to 15s to allow for Supabase retries
    } else {
      setLoadingTooLong(false);
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [authLoading, user, profile, profileLoading]);

  // 1. Ensure profile is fetched if user exists but profile is missing
  useEffect(() => {
    if (user && !profile && !profileLoading) {
      console.log("ProtectedRoute useEffect: Triggering profile fetch.");
      fetchUserProfile();
    }
  }, [user, profile, profileLoading, fetchUserProfile]);

  // 2. Enhanced Loading State: Wait for auth AND initial profile attempt
  // Show spinner if auth is loading OR if user is logged in but profile is still loading OR if user is logged in but profile is still null/undefined
  if (authLoading || (user && (profileLoading || !profile))) {
     console.log("ProtectedRoute: Showing loading spinner.", { authLoading, profileLoading, user: !!user, profile: !!profile });
     return (
      <Center h="100vh" flexDirection="column">
        <Spinner size="xl" mb={6} />
        <Text mb={4}>Loading your dashboard...</Text>
        
        {loadingTooLong && (
          <Button 
            onClick={() => {
              import('./utils/authUtils').then(({ clearAuthCache }) => {
                clearAuthCache();
              });
            }}
            colorScheme="blue"
            size="sm"
            mb={2}
          >
            Clear Cache & Reload
          </Button>
        )}
      </Center>
    );
  }
   console.log("ProtectedRoute: Loading complete.", { authLoading, profileLoading, user: !!user, profile: !!profile });

  // 3. Authentication Check (after loading state)
  if (!user) {
    console.log("ProtectedRoute: No user found, redirecting to login.");
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 4. Setup Completion Check (user is logged in, profile loading is done, and profile *should* exist)
  // Add an explicit check for profile existence after loading, although the loading condition should cover this.
  if (!profile) {
    // This case should ideally not be reached if the loading logic is correct, 
    // but handle it defensively. Maybe redirect to login or show an error.
    console.error("ProtectedRoute: Profile is unexpectedly null after loading.");
    // Optionally clear auth state and redirect to login
    // useAuthStore.getState().clearAuth(); // Example if you have a clearAuth action
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Now we know profile exists
  if (!profile.setup_completed) {
    if (location.pathname !== '/setup') {
       console.log("ProtectedRoute: Setup not complete, redirecting to /setup.");
      return <Navigate to="/setup" state={{ from: location }} replace />;
    } else {
       console.log("ProtectedRoute: Setup not complete, already on /setup page.");
    }
  } else {
     if (location.pathname === '/setup') {
        console.log("ProtectedRoute: Setup complete, redirecting from /setup to /dashboard.");
        return <Navigate to="/dashboard" replace />; 
     }
     console.log("ProtectedRoute: Setup complete, rendering children.");
  }

  return <>{children}</>;
};

// Auth route wrapper to prevent authenticated users from accessing login pages
const AuthRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading: authLoading } = useAuthStore();
  const { profile, profileLoading } = useUserStore(); 

  // Use the same enhanced loading check as ProtectedRoute
  if (authLoading || (user && (profileLoading || !profile))) { 
     return (
      <Center h="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  // Check if user is already authenticated - if so, redirect away from auth pages
  if (user) {
    console.log("AuthRoute: User already logged in, redirecting.");
    
    if (profile) {
      // If profile exists, check setup_completed status
      if (profile.setup_completed) {
        return <Navigate to="/dashboard" replace />;
      } else {
        return <Navigate to="/setup" replace />;
      }
    } else {
      // If profile doesn't exist, still show the spinner while loading
      // This prevents automatic redirection to setup if profile fetch fails
      console.log("AuthRoute: User logged in but no profile, waiting.");
      return (
        <Center h="100vh">
          <Spinner size="xl" />
        </Center>
      );
    }
  }

  // If no user, allow rendering the auth page (Login, Register, etc.)
  console.log("AuthRoute: No user, showing login page.");
  return <>{children}</>;
};

// Helper component to handle initial redirection logic
const AuthRedirector = () => {
  const { user, loading: authLoading } = useAuthStore();
  const { profile, profileLoading } = useUserStore();

   if (authLoading || (user && profileLoading && !profile)) {
     return (
      <Center h="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!profile || !profile.setup_completed) {
    return <Navigate to="/setup" replace />;
  }

  return <Navigate to="/dashboard" replace />;
};

function App() {
  const { checkSession } = useAuthStore();
  const { fetchUserProfile } = useUserStore();
  
  // Check for existing session on app load
  useEffect(() => {
    console.log('App mounted, checking initial session.');
    
    let isMounted = true;
    
    // Initial session check with better handling
    const initializeSession = async () => {
      try {
        const sessionExists = await checkSession();
        
        if (isMounted) {
          console.log('Session check completed:', sessionExists ? 'User is logged in' : 'No active session');
        }
      } catch (err) {
        console.error('Initial checkSession failed:', err);
        
        if (isMounted) {
          // Ensure loading is false even if checkSession fails
          useAuthStore.setState({ loading: false });
        }
      }
    };
    
    initializeSession();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, []);
  
  // Set up auth state listener
  useEffect(() => {
    console.log('Setting up onAuthStateChange listener.');
    
    // Setup the auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event, !!session);
      
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        if (session) {
          // Update auth store with session data
          useAuthStore.setState({ 
            user: session.user, 
            session: session, 
            loading: false 
          });
          
          // Then fetch profile if needed (don't await to avoid blocking)
          if (session.user) {
            fetchUserProfile().catch(err => 
              console.error('Error fetching profile after auth change:', err)
            );
          }
        } else {
          useAuthStore.setState({ loading: false });
        }
      } else if (event === 'SIGNED_OUT') {
        console.log('Signed out event received, clearing store.');
        useAuthStore.setState({ user: null, session: null, loading: false });
        useUserStore.setState({ 
          profile: null, 
          vacationDays: [], 
          dailyEntries: [], 
          dailyTargets: [], 
          calculations: null, 
          error: null,
          profileLoading: false,
        });
      }
    });
    
    // Clean up subscription on unmount
    return () => {
      console.log('Cleaning up onAuthStateChange listener.');
      subscription.unsubscribe();
    };
  }, []); // Removed fetchUserProfile dependency

  return (
    <ChakraProvider theme={theme}>
      <QueryClientProvider client={new QueryClient()}>
        <Router>
          <Routes>
            {/* Auth routes */}
            <Route path="/login" element={
              <AuthRoute>
                <LoginPage />
              </AuthRoute>
            } />
            <Route path="/register" element={
              <AuthRoute>
                <RegisterPage />
              </AuthRoute>
            } />
            <Route path="/verify-email" element={<EmailVerificationPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/auth-callback" element={<AuthCallbackPage />} />
            
            {/* Protected routes */}
            <Route 
              path="/setup" 
              element={
                <ProtectedRoute>
                  <SetupWizard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/settings" 
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/progress-overview" 
              element={
                <ProtectedRoute>
                  <ProgressOverviewPage />
                </ProtectedRoute>
              } 
            />
            
            {/* Default route */}
            <Route path="/" element={<AuthRedirector />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </QueryClientProvider>
    </ChakraProvider>
  );
}

export default App;
